import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { HttpStatusCode } from 'constantsValues';
import { PoliciesSummary, PolicyDetails, Total } from 'generated-api/bff/api'; // Updated import
import { getApiInstance } from 'services/Api';

// Async thunk for fetching policies
export const fetchPolicies = createAsyncThunk<PoliciesSummary>('policies/fetchPolicies', async () => {
	try {
		const apiInstance = await getApiInstance('/commercial-selfservice/');
		const response = await apiInstance.bffApiServiceLayer.getPolicies();

		if (!response?.data?.policies?.length) {
			return;
		}

		return {
			policies: response.data.policies,
			total: response.data.total, // Include the total object
		};
	} catch (error) {
		throw new Error('No policies found', { cause: error });
	}
});

interface PoliciesState {
	policies: PolicyDetails[] | null; // Updated to use PolicyDetails
	total: Total | null;
	status: HttpStatusCode | null;
	error: string | null;
}

const initialState: PoliciesState = {
	policies: null,
	total: null,
	status: HttpStatusCode.Idle,
	error: null,
};

// Slice for policies
const policiesSlice = createSlice({
	name: 'policies',
	initialState,
	reducers: {
		clearPoliciesCache(state) {
			state.policies = null;
			state.total = null;
			state.status = HttpStatusCode.Idle;
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchPolicies.pending, (state) => {
				state.status = HttpStatusCode.Loading;
				state.error = null;
			})
			.addCase(fetchPolicies.fulfilled, (state, action) => {
				if (!action?.payload?.policies || !action?.payload?.total) {
					state.status = HttpStatusCode.NoContent;
					return;
				}
				state.status = HttpStatusCode.OK;
				state.policies = action.payload.policies; // Set policies in the state
				state.total = action.payload.total; // Set total in the state
			})
			.addCase(fetchPolicies.rejected, (state, action) => {
				state.status = HttpStatusCode.InternalServerError;
				state.error = action.error.message || 'Failed to fetch policies';
			});
	},
});

export const { clearPoliciesCache } = policiesSlice.actions;
export default policiesSlice.reducer;
